import { MutableRefObject } from 'react'

import { LocationEditorActions, LocationEditorCallbacks, LocationEditorState } from '../../types'
import { getLocationDisplayText } from '../../utils/getLocationDisplayText'

export function createLocationEditorReducer(
  callbacksRef: MutableRefObject<LocationEditorCallbacks>,
) {
  return function locationEditorReducer(
    state: LocationEditorState,
    action: LocationEditorActions,
  ): LocationEditorState {
    switch (action.type) {
      case 'textfield:change': {
        callbacksRef.current?.onTextChange(action.text)

        return {
          ...state,
          textFieldValue: action.text,
          textFieldDirty: true,
        }
      }

      case 'textfield:clear': {
        return {
          ...state,
          dropdownOptions: state.dropdownOptions.filter(asset => asset.type !== 'place'),
          dropdownSelectedIndex: -1,
          textFieldValue: '',
        }
      }

      case 'textfield:click': {
        if (state.open) return state

        return {
          ...state,
          open: true,
        }
      }

      case 'textfield:keydown': {
        switch (action.event.code) {
          case 'Tab': {
            return {
              ...state,
              open: false,
            }
          }

          case 'Enter': {
            action.event.preventDefault()
            if (state.open === false) return state

            const selectedOption = state.dropdownOptions[state.dropdownSelectedIndex]

            if (selectedOption) {
              callbacksRef.current?.onSelectAsset(selectedOption)
            } else {
              const location = callbacksRef.current?.getLocation()

              // if no option is selected, we reset the text field value
              callbacksRef.current?.setLoading(false)
              return { ...state, textFieldValue: getLocationDisplayText(location), open: false }
            }

            return { ...state, open: false }
          }

          case 'Escape': {
            action.event.preventDefault()

            return { ...state, open: false }
          }

          case 'ArrowUp': {
            if (state.open === false) {
              return { ...state, open: true }
            }

            if (state.dropdownOptions.length === 0) {
              return state
            }

            action.event.preventDefault()

            const dropdownSelectedIndex = Math.max(state.dropdownSelectedIndex - 1, 0)
            return { ...state, dropdownSelectedIndex }
          }

          case 'ArrowDown': {
            if (state.open === false) {
              return { ...state, open: true }
            }

            if (state.dropdownOptions.length === 0) {
              return state
            }

            action.event.preventDefault()

            const dropdownSelectedIndex = Math.min(
              state.dropdownSelectedIndex + 1,
              state.dropdownOptions.length - 1,
            )
            return { ...state, dropdownSelectedIndex }
          }

          default:
            return state
        }
      }

      case 'arrowDropdown:click': {
        return { ...state, open: !state.open }
      }

      case 'assetListItem:click': {
        const asset = state.dropdownOptions.find(asset => asset.id === action.assetId)
        if (!asset) {
          console.error('asset not found')
          return state
        }

        callbacksRef.current?.onSelectAsset(asset)

        return { ...state, open: false }
      }

      case 'actionBar:click': {
        if (state.open === false) return state

        return { ...state, open: false }
      }
      case 'clickAway:click': {
        if (state.open === false) return state

        callbacksRef.current?.setLoading(false)
        const location = callbacksRef.current?.getLocation()
        return { ...state, textFieldValue: getLocationDisplayText(location), open: false }
      }

      case 'react/location:update': {
        const textFieldValue = getLocationDisplayText(action.location)

        if (state.textFieldValue === textFieldValue) {
          return state
        }

        return {
          ...state,
          textFieldValue,
          textFieldDirty: false,
        }
      }

      case 'react/assets:update': {
        const currentSelectedAsset = state.dropdownOptions[state.dropdownSelectedIndex]

        const dropdownOptions = action.assets
        const dropdownSelectedIndex = currentSelectedAsset
          ? dropdownOptions.findIndex(asset => asset.id === currentSelectedAsset.id)
          : -1

        return {
          ...state,
          dropdownOptions,
          dropdownSelectedIndex,
        }
      }

      default:
        return state
    }
  }
}
